/**
 * The words and numbers the checkout shows, as plain functions: the core
 * needs them for what it tells the page (a total, what paid), and every
 * renderer needs the same ones for what it draws.
 */

import type { BuyerSession } from "./state-machine";
import { WalletApiFailure } from "./wallet-api";

export function messageFromError(error: unknown, fallback: string) {
  const limit = limitRefusalMessage(error);
  if (limit) return limit;
  // A machine reason (`identity_unavailable`, `request_timeout`) is for logs,
  // never for the buyer: they get the sentence the caller wrote instead.
  if (error instanceof Error && /^[a-z]+(_[a-z]+)+$/.test(error.message))
    return fallback;
  if (error instanceof Error && error.message) return error.message;
  if (error && typeof error === "object" && "error" in error) {
    const nested = (error as { error?: { message?: unknown } }).error?.message;
    if (typeof nested === "string" && nested) return nested;
  }
  return fallback;
}

/** A limit refusal in plain words, or null for any other error: from the
 *  engine's envelope (`{ error: { code, detail } }`, and what carries it),
 *  or the wallet adapter's failure around it alike. For a page that has its
 *  own words for every other failure (the tickets sheets). */
export function limitRefusalMessage(error: unknown): string | null {
  return limitRefusal(
    error instanceof WalletApiFailure
      ? error.failure
      : error && typeof error === "object" && "error" in error
        ? (error as { error?: { code?: unknown; detail?: unknown } }).error
        : undefined,
  );
}

const tickets = (count: number) => `${count} ticket${count === 1 ? "" : "s"}`;

/** Plain words for the engine's event checkout limit refusals (LIS-924),
 *  and for a members' presale refused to a non-member (LIS-1156).
 *  `detail` carries the limit and, for the per-customer cap, what is left;
 *  without them the message still says what to do. */
function limitRefusal(
  error: { code?: unknown; detail?: unknown } | undefined,
): string | null {
  const detail = (error?.detail ?? {}) as Record<string, unknown>;
  const count = (key: string) => {
    const value = detail[key];
    return typeof value === "number" && Number.isInteger(value) ? value : null;
  };
  const limit = count("limit");
  const remaining = count("remaining");
  switch (error?.code) {
    case "order_quantity_below_minimum":
      return limit === null
        ? "This event needs more tickets in one order. Add tickets to continue."
        : `This event sells at least ${tickets(limit)} per order. Add tickets to continue.`;
    case "order_quantity_above_maximum":
      return limit === null
        ? "That's more tickets than this event allows in one order. Remove some to continue."
        : `This event allows up to ${tickets(limit)} per order. Remove some to continue.`;
    case "customer_ticket_limit_exceeded":
      if (remaining === 0)
        return limit === null
          ? "You've already bought the most tickets this event allows per customer."
          : `You've already bought the most tickets this event allows: ${tickets(limit)} per customer.`;
      if (remaining !== null)
        return `You can buy ${remaining} more ${remaining === 1 ? "ticket" : "tickets"} for this event${limit === null ? "" : ` (${tickets(limit)} per customer)`}. Lower the quantity to continue.`;
      return "That's more tickets than this event allows per customer. Lower the quantity to continue.";
    // A members' presale (LIS-1156) the buyer holds no fan club membership
    // for, or bought without signing in as the member.
    case "members_only":
      return "This ticket is for fan club members. Sign in with the account you joined the club with to buy it.";
    default:
      return null;
  }
}

/** Each event's ticket count in this checkout against its per-order limits
 *  (LIS-924): the lines of the event's items together, never one line alone.
 *  Lines without an event do not count. */
export function eventOrderCounts(session: Pick<BuyerSession, "line_items">) {
  const events = new Map<
    string,
    { id: string; name: string; total: number; min: number | null; max: number | null }
  >();
  for (const line of session.line_items) {
    const event = line.event;
    if (!event) continue;
    const entry = events.get(event.id) ?? {
      id: event.id,
      name: event.name,
      total: 0,
      min: event.min_tickets_per_order ?? null,
      max: event.max_tickets_per_order ?? null,
    };
    entry.total += line.quantity;
    events.set(event.id, entry);
  }
  return [...events.values()];
}

/** Why this checkout cannot complete as it stands, or null when it can. The
 *  engine refuses every cart outside an event's per-order limits, so no way
 *  to pay or complete is offered until the tickets sheet fits it. */
export function orderLimitProblem(session: Pick<BuyerSession, "line_items">) {
  for (const event of eventOrderCounts(session)) {
    const problem = eventLimitProblem(event);
    if (problem) return problem;
  }
  return null;
}

/** Why one event's tickets in an order fall outside its per-order limits,
 *  or null when they fit: the checkout's words, and the tickets sheets'
 *  before it opens. */
export function eventLimitProblem(event: {
  name: string;
  total: number;
  min: number | null;
  max: number | null;
}) {
  if (event.min !== null && event.total < event.min)
    return `Choose at least ${tickets(event.min)} for ${event.name}.`;
  if (event.max !== null && event.total > event.max)
    return `Choose at most ${tickets(event.max)} for ${event.name}.`;
  return null;
}

export function money(
  minor: number | null,
  currency: string | null,
  locale = "en-GB",
) {
  const code = currency ?? "GBP";
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: code,
    }).format((minor ?? 0) / 100);
  } catch {
    return `${((minor ?? 0) / 100).toFixed(2)} ${code}`;
  }
}

/** A complete address is worth telling the engine about; a half-typed one
 *  is not. */
export const isEmailValid = (value: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());

/** "Visa 4242": how a saved card is named on the pay button and to the host. */
export function cardLabel(card: { brand: string; last4: string }) {
  return `${brandName(card.brand)} ${card.last4}`;
}

export function brandName(brand: string) {
  const known: Record<string, string> = {
    amex: "Amex",
    american_express: "Amex",
    diners: "Diners",
    discover: "Discover",
    jcb: "JCB",
    mastercard: "Mastercard",
    unionpay: "UnionPay",
    visa: "Visa",
  };
  return (
    known[brand.toLowerCase()] ??
    `${brand.charAt(0).toUpperCase()}${brand.slice(1)}`
  );
}
